/**
 * The trip, reduced to a drawing.
 *
 * The closing card used to throw away the one thing it had that a competitor's cannot copy: the
 * journey the viewer has just watched. A pre-rendered outro knows nothing about the trip. This one
 * is a composition sitting on a live map, so as the map dissolves the route can stay behind as a
 * line -- the same shape, now a keepsake rather than a camera move.
 */

export type Outline = {
	/** One SVG path per leg, so a flight and a road do not get joined by a phantom line. */
	legs: string[];
	/** Where each stop sits, and which destination it is -- so a photo can be hung on it. */
	stops: { x: number; y: number; dest: number }[];
	width: number;
	height: number;
};

/** Length of a path in kilometres, so legs can be compared with each other. */
const pathKm = (path: number[][]) => {
	const R = 6371.0088;
	const rad = Math.PI / 180;
	let total = 0;
	for (let i = 1; i < path.length; i++) {
		const [a, b] = [path[i - 1], path[i]];
		const [p1, p2] = [a[1] * rad, b[1] * rad];
		const h = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(((b[0] - a[0]) * rad) / 2) ** 2;
		total += 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
	}
	return total;
};

/** Interpolate along the shortest path over the sphere -- the line a plane actually takes. */
const greatCircle = (a: number[], b: number[], samples = 64): number[][] => {
	const rad = Math.PI / 180;
	const [lon1, lat1, lon2, lat2] = [a[0] * rad, a[1] * rad, b[0] * rad, b[1] * rad];
	const d =
		2 *
		Math.asin(
			Math.sqrt(Math.sin((lat2 - lat1) / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2)
		);
	if (!d) return [a, b];
	const out: number[][] = [];
	for (let i = 0; i <= samples; i++) {
		const f = i / samples;
		const A = Math.sin((1 - f) * d) / Math.sin(d);
		const B = Math.sin(f * d) / Math.sin(d);
		const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
		const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
		const z = A * Math.sin(lat1) + B * Math.sin(lat2);
		out.push([Math.atan2(y, x) / rad, Math.atan2(z, Math.hypot(x, y)) / rad]);
	}
	return out;
};

/**
 * Mercator, so the drawing has the same proportions the map did -- and returned in degrees, because
 * x is longitude in degrees. Returning the raw logarithm mixes radians against degrees, and the two
 * differ by a factor of 57: every route came out squashed flat, which read as a projection that had
 * lost the trip rather than as the unit error it was.
 */
const mercatorY = (lat: number) => {
	const clamped = Math.max(-85, Math.min(85, lat));
	return (Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360)) * 180) / Math.PI;
};

/**
 * Fit every leg into a box, preserving aspect. A trip that runs east-west should come out wide and
 * one that runs north-south tall; stretching either to fill the box would draw a different journey.
 */
export const routeOutline = (reelData: any[], width: number, height: number, padding = 40): Outline => {
	// Carry the original index through the filter: a stop has to be able to say which destination it
	// is, or a photo cannot be hung on it.
	const indexed = (reelData || [])
		.map((d, i) => ({ path: d?.path, i }))
		.filter((e) => Array.isArray(e.path) && e.path.length >= 2);
	const raw: number[][][] = indexed.map((e) => e.path);

	// Keep the part of the trip that is a route, and drop the part that is transport.
	//
	// Drawn literally, one intercontinental hop swallows everything: Schiphol to Bangkok spans 95
	// degrees of longitude while the bar crawl spans 0.05, so the crawl and the island collapsed onto
	// a single horizontal rule and the "outline" was a line with two dots. Two rules fix it, and both
	// are relative so no threshold has to be guessed per trip.
	//
	// First, drop the outliers: a leg more than six times the median is a different kind of movement
	// from the rest of the trip. Then keep the longest unbroken run of what survives -- a trip with
	// two clusters an ocean apart has no single shape, and the honest thing to draw is the place the
	// traveller actually moved around in, not the gap between two of them.
	const km = raw.map(pathKm);
	const sorted = [...km].sort((a, b) => a - b);
	const median = sorted[Math.floor(sorted.length / 2)] || 0;
	const kept = km.map((d) => median > 0 && d <= median * 6);

	let bestStart = 0;
	let bestLen = 0;
	let runStart = 0;
	let runLen = 0;
	kept.forEach((ok, i) => {
		if (ok) {
			if (runLen === 0) runStart = i;
			runLen++;
			if (runLen > bestLen) {
				bestLen = runLen;
				bestStart = runStart;
			}
		} else {
			runLen = 0;
		}
	});

	const chosen = bestLen > 0 ? indexed.slice(bestStart, bestStart + bestLen) : indexed;
	const legs: number[][][] = chosen
		.map((e) => e.path)
		// A two-point leg is a flight, and the film draws it as a great circle, not as a straight
		// line. Taking the two points literally turned Schiphol to Bangkok into a horizontal rule and
		// collapsed everything else onto it. Sample the same curve the map flies.
		.map((p: number[][]) => (p.length === 2 ? greatCircle(p[0], p[1]) : p));
	if (!legs.length) return { legs: [], stops: [], width, height };

	const pts = legs.flat();
	const xs = pts.map((p) => p[0]);
	const ys = pts.map((p) => mercatorY(p[1]));
	const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];

	const boxW = width - padding * 2;
	const boxH = height - padding * 2;
	const spanX = Math.max(maxX - minX, 1e-6);
	const spanY = Math.max(maxY - minY, 1e-6);
	const k = Math.min(boxW / spanX, boxH / spanY);
	const offX = padding + (boxW - spanX * k) / 2;
	const offY = padding + (boxH - spanY * k) / 2;

	const project = (p: number[]) => ({
		x: offX + (p[0] - minX) * k,
		// Screen y grows downwards, Mercator y grows north, so this flips.
		y: offY + (maxY - mercatorY(p[1])) * k,
	});

	const paths = legs.map((leg) => leg.map((p, i) => `${i === 0 ? "M" : "L"}${project(p).x.toFixed(1)},${project(p).y.toFixed(1)}`).join(" "));

	// A stop is where one leg ends and the next begins, so the ends of every leg cover them all. The
	// start of leg i is destination i and its end is destination i + 1.
	const stops = legs.flatMap((leg, n) => [
		{ ...project(leg[0]), dest: chosen[n].i },
		{ ...project(leg[leg.length - 1]), dest: chosen[n].i + 1 },
	]);
	const unique = stops.filter((s, i) => stops.findIndex((o) => Math.hypot(o.x - s.x, o.y - s.y) < 4) === i);

	return { legs: paths, stops: unique, width, height };
};
